import { inject, BindingEngine } from 'aurelia-framework';
import { QuoteService } from 'pages/quotes/service';
import { EventAggregator } from 'aurelia-event-aggregator';
import { DialogController } from 'aurelia-dialog';
import { DialogService } from 'aurelia-dialog';
import { Router } from 'aurelia-router';
import toastr from 'toastr';
import moment from 'moment';
import { formatTableDate, createButton, formatTableLink } from 'resources/utility';
import { delegate } from 'tippy.js';

@inject(DialogController, EventAggregator, DialogService, QuoteService, Router, BindingEngine)
export class QuoteEdit {
    constructor(controller, ea, dialogService, quoteService, router, bindingEngine) {
        this.controller = controller;
        this.ea = ea;
        this.dialogService = dialogService;
        this.quoteService = quoteService;
        this.quote = {};
        this.showSection = "structure";
        this.router = router;

        this.snapshotFilterOptions = {
            showDeleted: false
        }

        this.isReadonly = false;

        this.ea.subscribe('quote:save', async () => {
          this.save(false);
        });

        bindingEngine.propertyObserver(this.snapshotFilterOptions, 'showDeleted').subscribe(async () => {
          this.snapshotGrid.refresh();
        });

        this.snapshotColumns = [
          { dataField: 'description', cellTemplate: (container, options) => {
            const info = document.createElement('i');
            info.classList.add('bx', 'bx-info-circle', 'bx-sm', 'snapshot-description');
            info.setAttribute('data-message', options.data.description);
            container.append(info);
          }},
          { dataField: 'snapshotType', cellTemplate: (container, options) => formatTableLink({ container, containerOptions: options, callback: (data) => {
            window.open(`quote/edit/${data.quoteId}?snapshotId=${data.id}`, '_blank');
          } }) },
          { dataField: 'productionType' },
          { dataField: 'effectiveDate', customizeText: formatTableDate },
          { dataField: 'poNumber' },
          { dataField: 'revision' },
          { dataField: 'createdAt', customizeText: formatTableDate, sortOrder: 'desc' },
          { dataField: 'createdBy' },
          { caption: 'Actions', cellTemplate: (container, options) => {
            const div = document.createElement('div');
            if (options.data.deleted) {
              const restoreButton = createButton({ container, containerOptions: options, style: 'success', label: 'Restore', action: this.restoreSnapshot });
              restoreButton.style.marginRight = '20px';
              div.appendChild(restoreButton);
            } else {
              const deleteButton = createButton({ container, containerOptions: options, style: 'danger', label: 'Delete', action: this.deleteSnapshot });
              deleteButton.style.marginRight = '20px';
              div.appendChild(deleteButton);
            }
            const editButton = createButton({ container, containerOptions: options, style: 'primary', label: 'Edit', action: this.editSnapshot });
    
            div.appendChild(editButton);
            container.append(div);
          }, allowExporting: false }
        ];
    }

    handleChildSave = (e) => {
      if((/^react-devtools/gi).test(e.data.source)) return;
      this.save(false);
    }

    async activate(params) {
      if (params?.snapshotIdentifier != null) {
        this.snapshot = await this.quoteService.getSnapshotByIdentifier(params.id, params.snapshotIdentifier);
        this.quote = await this.quoteService.getSnapshotData(params.id, this.snapshot.id);
        this.isReadonly = true;
      } else if (params?.snapshotId != null) {
        this.quote = await this.quoteService.getSnapshotData(params.id, params.snapshotId);
        this.snapshot = await this.quoteService.getSnapshotById(params.id, params.snapshotId);
        this.isReadonly = true;
      } else if (params?.id != null) {
        this.quote = await this.quoteService.getById(params.id);
        this.name = window.localStorage.getItem('name');
        if (this.quote.lockedAt != null && this.quote.lockedBy.toLowerCase() != this.name.toLowerCase() && moment().utc().diff(moment(this.quote.lockedAt), 'minutes') < 20) {
          this.isReadonly = true;
          this.lockTimeCheck()
          this.lockTimer = setInterval(this.lockTimeCheck, 1000);
        }
      }

      if (this.quote.rawMaterialId == null) {
        this.quote.rawMaterial = null;
      }

      this.isChild = params.isChild;

      window.addEventListener('message', this.handleChildSave);
    }

    attached() {
      delegate('body', {
        target: '.snapshot-description',
        content(reference) {
            const message = reference.getAttribute('data-message');
            return message;
        }
      });
    }

    lockTimeCheck = () => {
      const targetTime = moment().utc().subtract(20, 'minutes');
      const diff = moment(this.quote.lockedAt).diff(targetTime, 'seconds');

      if (diff <= 0) {
        clearInterval(this.lockTimer);
        return;
      }

      this.lockMinutes = Math.floor(diff / 60);
      const lockSeconds = diff % 60;
      this.lockSeconds = lockSeconds < 10 ? `0${lockSeconds}` : lockSeconds;
    }

    async deactivate() {
      window.removeEventListener('message', this.handleChildSave);
      await this.quoteService.unlock(this.quote.id);
    }

    showEditSection(event, section) {
      Array.from(document.querySelectorAll('.nav-link')).forEach((element) => element.classList.remove('active'));
      event.target.classList.add('active');
      this.showSection = section;    
      this.ea.publish('show-section:change', section);
    }

    addSnapshot() {
      this.dialogService.open({
        viewModel: PLATFORM.moduleName('pages/quotes/edit-snapshot/index'),
        model: { quoteId: this.quote.id }
      }).whenClosed(async result => {
        if (!result.wasCancelled) {
          await this.quoteService.createSnapshot(this.quote.id, result.output);
          this.snapshotGrid.refresh();
        }
      });
    }

    editSnapshot = (item) => {
      this.dialogService.open({
        viewModel: PLATFORM.moduleName('pages/quotes/edit-snapshot/index'),
        model: { quoteId: this.quote.id, snapshot: item }
      }).whenClosed(async result => {
        if (!result.wasCancelled) {
          await this.quoteService.updateSnapshot(this.quote.id, result.output);
          this.snapshotGrid.refresh();
        }
      });
    }

    deleteSnapshot = (item) => {
      this.dialogService.open({
        viewModel: PLATFORM.moduleName('resources/elements/modals/confirm/index'),
        model: { message: 'Are you sure you want to delete this snapshot?' }
      }).whenClosed(async result => {
        if (!result.wasCancelled) {
          await this.quoteService.deleteSnapshot(this.quote.id, item.id);
          this.snapshotGrid.refresh();
        }
      });
    }

    restoreSnapshot = (item) => {
      this.quoteService.restoreSnapshot(this.quote.id, item.id);
      this.snapshotGrid.refresh();
    }

    volumeChange(item) {
      console.log(item);
    }

    print() {
      if (this.quote.isSnapshot) {
        window.open(`quote/print/${this.quote.id}?print=true&snapshotId=${this.snapshot.id}`);
      } else {
        window.open(`quote/print/${this.quote.id}?print=true`);
      }
    }

    goBack() {
      this.router.navigateToRoute('quotes');
    }

    async save(redirect) {
      //return;
      if (this.isReadonly) return;
      if (this.isRequesting) return;
      this.isRequesting = true;
      try {
          if (this.quote.id) {
              await this.quoteService.update(this.quote);
              toastr.success('Quote was updated!');
          }
          this.ea.publish('quotes:updated', this.quote);
          if (redirect) {
            if (this.isChild) {
              window.opener.postMessage('saveQuote');
              window.close();
            } else {
              this.router.navigateToRoute('quotes');
            }
            return;
          }
          this.quote = await this.quoteService.getById(this.quote.id);
          this.ea.publish('quote:reload');
          this.controller.ok(this.quote);
      } catch {
          this.isRequesting = false;
      }
     // }
      // this.isRequesting = false;
  }

}
